<template>
  <v-container fluid justify-start>
    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="organizations"
      :loading="isLoading"
      :search="search"
      :custom-filter="customFilter"
      :no-data-text="noDataText"
      item-key="id"
      :loading-text="getExpression('Alert', 'LoadingItems')"
      :footer-props="{
        'items-per-page-text': getExpression('Label', 'RowsPerPage')
      }"
      class="elevation-2"
      >
      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

      <template v-slot:top>

        <!-- Header Toolbar -->
        <v-toolbar dense flat dark :color="toolbarColor">
         
          <app-back-button v-bind:color="toolbarColorDark" />
         
          <v-toolbar-title :class="titleClass">Organizations</v-toolbar-title>

          <v-spacer />

          <!-- Edit Dialog -->
          <v-toolbar-items>
            <v-dialog
              v-model="editDialog"
              :max-width="dialogWidth"
              :persistent="true">

              <template v-slot:activator="{ on }">
                <v-btn v-if="isSmallDevice"
                  small
                  slot="activator"
                  :disabled="isLoading"
                  :color="toolbarColorDark"
                  class="mb-1 mt-0 mr-0 elevation-0"
                  v-on="on"
                  >New
                </v-btn>
                <v-btn v-else
                  slot="activator"
                  :disabled="isLoading"
                  :color="toolbarColorDark"
                  class="mb-1 mt-0 mr-0 elevation-0"
                  v-on="on"
                  >Create Organization
                </v-btn>
              </template>

              <v-card>
                <v-toolbar dense dark flat :color="toolbarColor">
                  <v-toolbar-title>
                    <v-icon class="mr-2">edit</v-icon>
                      {{ formTitle }}
                  </v-toolbar-title>
                </v-toolbar>

                <v-card-text class="pb-0 px-2">
                  <v-container grid-list-md>
                    <form>

                      <!-- Organization Name -->
                      <v-row no-gutters>
                        <v-col cols="12" sm="12">
                          <v-text-field
                            v-model="editedOrganization.name"
                            :label="getExpression('Label', 'Name')"
                            hide-details="auto"
                            class="mb-4"
                            ref="name"
                            :error-messages="errors.first('name')"
                            v-validate="'required|max:200'"
                            data-vv-name="name"
                          />
                        </v-col>
                      </v-row>
                    </form>
                  </v-container>
                </v-card-text>
                <v-card-actions class="pb-3">

                  <v-spacer />
                 
                  <v-btn @click="closeDialog" :loading="isSaving" class="rounded-0" large>{{ getExpression('Button', 'Cancel') }}</v-btn>
                  <v-btn :color="appColor_Dark" dark @click="save" :loading="isSaving" class="rounded-0 ml-4" large>{{ getExpression('Button', 'Save') }}</v-btn>
               
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Filters -->
          <v-row no-gutters class="mx-3 mt-1">

            <!-- Search -->
            <v-col cols="12" md="12">
              <v-text-field
                v-model="search"
                append-icon="search"
                :label="getExpression('Label', 'Search')"
                single-line
                hide-details
                :disabled="isLoading"
                clearable
                class="mb-3"
              />
            </v-col>
          </v-row>
      </template>

      <template v-slot:item.name="{ item }">
        <span class="text-left" style="white-space: nowrap">
          <span @click="edit(item)">
            <span :style="appStyle_HyperLink">{{ item.name }}</span>
            <v-icon small style="cursor: pointer" class="ml-2">edit</v-icon>
          </span>
        </span>
      </template>

      <template v-slot:item.createDate="{ item }">
        <span class="text-left"><app-date :date="item.createDate" :format="'MMM-DD-YYYY'" /></span>
      </template>

      <template v-slot:item.action="{ item }">
          <v-icon @click="remove(item)">delete</v-icon>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import { mapGetters } from 'vuex'
import AppDate from '@/components/App/AppDate'
import AppBackButton from '@/components/App/AppBackButton'
import { AppColor, AppStyle, LanguageId } from '@/config.js'

export default {
  name: 'OrganizationManager',
  components: {
    AppDate,
    AppBackButton
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {
      // colors
      appColor_Medium: AppColor.Medium,
      appColor_Dark: AppColor.Dark,
      appColor_Progress: AppColor.Progress,

      // styles
      appStyle_HyperLink: AppStyle.HyperLink,

      isLoading: true,
      isSaving: false,
      isDeleting: false,
      search: '',
      editDialog: false,
      organizations: [],
      organizationId: null,


      editedIndex: -1,
      editedOrganization: {
        id: -1,
        name: null,
        domains: null,
        createDate: null
      },
      defaultOrganization: {
        id: -1,
        name: null,
        domains: null,
        createDate: null
      },
      restoreOrganization: {
        id: -1,
        name: null,
        domains: null,
        createDate: null
      },
      headers: [
        {
          text: 'Organization',
          value: 'name',
          align: 'left',
          width: '15%',
          class: 'pr-0'
        },
        {
          text: 'Created',
          value: 'createDate',
          align: 'left',
          width: '10%',
          class: 'pr-0'
        },
        {
          value: 'action',
          sortable: false,
          align: 'center',
          width: '5%'
        }
      ],
    }
  },
  computed: {
    ...mapGetters([
      'profile',
      'locale']),
    noDataText () {
      if (this.isLoading) return this.getExpression('Alert', 'OneMoment')

      if (this.organizations.length === 0) {
        return this.getExpression('Alert', 'NoOrganizations')
      }
      return ''
    },
    formTitle () {
      return this.editedIndex < 0 ? 'New Organization' : 'Edit Organization'
    },
    dictionary () {
      return {
        attributes: {
          name: 'Name',
          organization: 'Organization'
        },
        custom: {
          name: {
            required: () => this.getExpression('Validation', 'NameRequired'),
            max: this.getExpression('Validation', 'NameMaxChars').replace('{0}', '200'),
            alpha_spaces: this.getExpression('Validation', 'NameAlphaSpaces')
          }
        }
      }
    },
    toolbarColor () {
      return this.appColor_Medium
    },
    toolbarColorDark () {
      return this.appColor_Dark
    },
    // styles
    dialogWidth () {
      return this.profile.languageId === LanguageId.English
        || this.editedIndex < 0
        ? '380px'
        : '480px'
    },
    titleClass () {
      return this.isSmallDevice
        ? 'body-2'
        : ''
    }
  },
  methods: {
    loadData () {
      const self = this
      self.isLoading = true

      appService.getOrganizationsForManager().then(data => {
        if (data.errorSeverity !== null) {
          self.handleError(data)
        } else {
          self.organizations = data.organizations
        }
        self.isLoading = false
      })
    },

    edit (item) {
      const self = this
      self.editedIndex = self.organizations.indexOf(item)
      self.editedOrganization = Object.assign({}, item)
      self.restoreOrganization = Object.assign({}, item)
      self.editDialog = true
    },

    save () {
      const self = this
      self.$validator.resume()
      self.$validator.validateAll().then(valid => {
        if (valid) {
            self.isSaving = true
            // Scrub html tags from text input
            self.editedOrganization.name = self.editedOrganization.name.toString()
              .trim().replace(/</g, "&lt;")
              .replace(/>/g, "&gt;")
            
            const isEditMode = self.editedIndex > -1
            if (isEditMode) {
              var editedOrganization = self.organizations[self.editedIndex]
              Object.assign(editedOrganization, self.editedOrganization)
              const editModel = self.getModel(self.editedOrganization, isEditMode)

              appService.updateOrganization(editedOrganization.id, editModel).then(data => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
                Object.assign(self.editedOrganization, self.defaultOrganization)
              } else {
                self.closeDialog()
              }
              self.isSaving = false
              self.$validator.pause()
              }).catch(e => { self.handleCriticalError(e) })
            } else {
              const addModel = self.getModel(self.editedOrganization, isEditMode)
  
              appService.addOrganization(addModel).then(data => {
                if (data.errorSeverity !== null) {
                  self.handleError(data)
                } else {
                  self.closeDialog()
                  var newOrganization = Object.assign({}, data.organization)
                  self.organizations.unshift(newOrganization)
                }
                self.isSaving = false
                self.$validator.pause()
              }).catch(e => { self.handleCriticalError(e) })
            }
          }
      })
    },

    remove (item) {
      const self = this
      const index = self.organizations.indexOf(item)
      const deleteText = self.generateDeleteText(self.organizations[index])
      
      self.$confirm.show({
        title: 'Delete Organization?',
        text: deleteText,
        onConfirm: () => {
          self.validateDelete(item).then((result) => {
            if (result.errorSeverity !== null) {
              self.handleError(result)
            } else {
              self.isDeleting = true
              appService.deleteOrganization(item.id).then((data) => {
                if (data.errorSeverity > 0) {
                  self.handleError(data)
                } else {
                  self.organizations.splice(index, 1)
                }
                self.isDeleting = false
              }).catch(e => { self.handleCriticalError(e) })
            }
          })
        }
      })
    },

    validateDelete(org) {
      return new Promise((resolve) => {
        const self = this

        self.validateFacilitiesForOrg(org.id, org.name).then(data => {
          if (data.errorSeverity !== null) {
            resolve(data)
          } else {
            self.validateUsersForOrg(org.id, org.name).then(result => {
              resolve(result)
            })
          }
        })
      })
    },

    validateFacilitiesForOrg(orgId, orgName) {
      return new Promise(resolve => {
        appService.getFacilitiesByOrganization(orgId).then(data =>{
          if (data.errorSeverity !== null) {
            resolve(data)
          } else {
            const facilities = data.items
            if (facilities.length === 0) {
              resolve({ errorSeverity: null })
            }

            // pluralize
            const result = facilities.length === 1
                ? `<b>${orgName}</b> still has <b>1 Facility</b> assigned to it.<br/><br/>Please unassign the facility first.`
                : `<b>${orgName}</b> still has <b>${facilities.length} Facilities</b> assigned to it.<br/><br/>Please unassign the facilities first.`

            resolve({ 
              errorMessage: result,
              errorSeverity: 0
            })
          }
        })
      })
    },

    validateUsersForOrg(orgId, orgName) {
      return new Promise(resolve => {
        appService.getDomainManagersForOrganization(orgId).then(data =>{
          if (data.errorSeverity !== null) {
            resolve(data)
          } else {
            if (data.users.length === 0) {
              resolve({ errorSeverity: null })
            }

            // pluralize
            const result =
              data.users.length === 1
                ? `<b>${orgName}</b> still has <b>1 Domain Manager</b> assigned to it.<br/><br/>Please unassign the user first.`
                : `<b>${orgName}</b> still has <b>${data.users.length} Domain Managers</b> assigned to it.<br/><br/>Please unassign the users first.`

            resolve({ 
              errorMessage: result,
              errorSeverity: 0
            })
          }
        })
      })
    },

    generateDeleteText(organization) {
      if (!this.isNullOrUndefined(organization.domain)) return ''

      var text = `<b>${organization.name}</b></br></br>The following domains will also be deleted:<ul>`

      organization.domains.forEach((d) => {
        text += `<li>${d.name}</li>`
      })

      text += '</ul>'
      return text
    },

    getModel (o, isEditMode) {
      if (isEditMode) {
        return {
          id: o.id,
          name: o.name.toString().trim().replace(/</g, "&lt;").replace(/>/g, "&gt;"),
          createDate: o.createDate
        }
      } else {
        return {
          id: o.id,
          name: o.name.toString().trim().replace(/</g, "&lt;").replace(/>/g, "&gt;"),
        }
      }
    },

    customFilter (value, search, item) {
      return (
        (value != null &&
          search != null &&
          item.name
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1)
      )
    },

    closeDialog () {
      this.editDialog = false
      setTimeout(() => {
        this.editedIndex = -1
        this.resetEditedOrganization()
      }, 300)
    },

    resetEditedOrganization () {
      this.editedOrganization = Object.assign({}, this.defaultUser)
      this.editedIndex = -1
      this.$validator.reset()
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? this.getExpression('FormTitle', 'ValidationFailed')
          : this.getExpression('FormTitle', 'InternalError'),
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  },
  beforeDestroy() {
    this.$validator.pause()
  },
  mounted() {
    this.$validator.localize('en', this.dictionary)
    this.$validator.reset()
  },
  watch: {
    editDialog (val) {
      if (val) {
        const self = this
        self.$validator.reset()
        self.$validator.pause()
        // if (self.editedIndex < 0) self.resetEditedOrganization()
        setTimeout(() => {
          self.$refs.name.focus()
        }, 300)
      } else {
        this.closeDialog()
      }
    }
  }
}
</script>

<style>
.truncate {
  max-width: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>


